import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, Avatar, Chip, Stack, Button, CircularProgress, Dialog, DialogContent, Alert, Checkbox, FormControlLabel } from "@mui/material";
import PsychologistApi, { Psychologist } from "../api/PsychologistApi";
import SchedulingApi, { Slot } from "../api/SchedulingApi";
import AppointmentApi, { AppointmentType } from "../api/AppointmentApi";
import { useAuth } from "../context/AuthContext";
import AuthPanel from "../components/AuthPanel";
import ReportProblemDialog from "../components/ReportProblemDialog";
import OrgApi, { OrgMembershipInfo } from "../api/OrgApi";
import MutualiteitApi, { ReimbursementPreview } from "../api/MutualiteitApi";
import { eur } from "../constants/funnel";

const TYPES: { key: AppointmentType; min: number }[] = [
  { key: "intro", min: 15 },
  { key: "coaching", min: 30 },
  { key: "therapy", min: 50 },
];

export default function Booking() {
  const { psychologistId } = useParams<{ psychologistId: string }>();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fundCode = (location.state as any)?.fundCode as string | undefined;

  const [psy, setPsy] = useState<Psychologist | null>(null);
  const [type, setType] = useState<AppointmentType>("intro");
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [selected, setSelected] = useState("");
  const [needAuth, setNeedAuth] = useState(false);
  const [wantBook, setWantBook] = useState(false);
  const [busy, setBusy] = useState(false);
  const [coverage, setCoverage] = useState<OrgMembershipInfo | null>(null);
  const [preview, setPreview] = useState<ReimbursementPreview | null>(null);
  // WER VI.46 §8: express request to perform the session within the 14-day
  // withdrawal period. Must start UNTICKED and be ticked before paying.
  const [withdrawalConsent, setWithdrawalConsent] = useState(false);
  // Usage-based employer model: the server signalled that the employer bundle
  // (incl. the anonymous overage pool) is used up → this booking is self-paid.
  const [fundingExhausted, setFundingExhausted] = useState(false);
  // The server already created a self-paid booking while we expected coverage:
  // ask for an explicit "continue to payment" instead of auto-redirecting.
  const [pendingCheckout, setPendingCheckout] = useState("");

  useEffect(() => {
    if (psychologistId) PsychologistApi.get(psychologistId).then(setPsy).catch(() => {});
  }, [psychologistId]);

  useEffect(() => {
    if (user) OrgApi.membership().then(setCoverage).catch(() => setCoverage(null));
    else setCoverage(null);
  }, [user]);

  useEffect(() => {
    if (!psychologistId) return;
    setLoadingSlots(true);
    setSelected("");
    setPendingCheckout("");
    SchedulingApi.slots(psychologistId, type)
      .then(setSlots)
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [psychologistId, type]);

  const priceFor = (tp: AppointmentType) =>
    tp === "intro" ? 0 : tp === "coaching" ? psy?.coachingRateCents ?? psy?.rateCents ?? 3750 : psy?.therapyRateCents ?? psy?.rateCents ?? 3750;
  const priceCents = priceFor(type);
  const visibleTypes = TYPES.filter((tp) => tp.key !== "coaching" || psy?.offersCoaching !== false);
  const remaining = coverage?.remaining ?? 0;
  // A paid session is employer-funded when the member still has covered sessions
  // left — or, usage-based, when the org's anonymous overage pool can fund it.
  const coveredFor = (tp: AppointmentType) =>
    tp !== "intro" && !!coverage && !fundingExhausted && (remaining > 0 || coverage.poolAvailable === true);
  // Employer bundle for this year is used up → self-pay with a neutral note
  // (mutualiteit reimbursement applies to what the member now pays).
  const selfPayTransition = !!coverage && type !== "intro" && !coveredFor(type);
  // The client actually pays for this booking (not free, not employer-covered).
  const isPaid = priceCents > 0 && !coveredFor(type);
  // Sessions start within 14 days of booking almost always → §8 consent needed.
  const withinWithdrawalPeriod = !!selected && new Date(selected).getTime() - Date.now() < 14 * 24 * 60 * 60 * 1000;
  const needsWithdrawalConsent = isPaid && withinWithdrawalPeriod;

  // Reimbursement preview, gated by this provider's vetted credentials.
  useEffect(() => {
    const covered = coveredFor(type);
    if (!fundCode || fundCode === "none" || priceCents <= 0 || covered || !psychologistId) { setPreview(null); return; }
    MutualiteitApi.reimbursementPreview(fundCode, priceCents, psychologistId).then(setPreview).catch(() => setPreview(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fundCode, priceCents, type, coverage, psychologistId, fundingExhausted]);

  const performBook = async () => {
    setBusy(true);
    try {
      const res = await AppointmentApi.book({
        psychologistId: psychologistId!,
        type,
        start: selected,
        fundCode,
        withdrawalConsent: needsWithdrawalConsent ? withdrawalConsent : undefined,
      });
      if (res.checkoutUrl) {
        if (res.employerFundingExhausted && !isPaid) {
          // We showed "covered", the server decided self-pay (bundle just ran
          // out). Don't silently push the user into a payment they didn't see
          // coming — explain + explicit "continue to payment".
          setFundingExhausted(true);
          setPendingCheckout(res.checkoutUrl);
          setBusy(false);
          return;
        }
        window.location.href = res.checkoutUrl;
        return;
      }
      navigate(`/afspraak/${res.appointmentId}?new=1`);
    } catch (e: any) {
      // Employer bundle used up + the now-required §8 consent was missing:
      // re-render the booking as self-pay so the user can re-confirm.
      if (e?.response?.data?.code === "employer_funding_exhausted") setFundingExhausted(true);
      setBusy(false);
    }
  };

  // After successful auth, continue the booking the user intended.
  useEffect(() => {
    if (user && wantBook) {
      setWantBook(false);
      setNeedAuth(false);
      performBook();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, wantBook]);

  const onConfirm = () => {
    if (!selected) return;
    if (!user) {
      setWantBook(true);
      setNeedAuth(true);
      return;
    }
    performBook();
  };

  // Group slots by calendar day.
  const byDay = useMemo(() => {
    const map = new Map<string, Slot[]>();
    for (const s of slots) {
      const day = s.start.slice(0, 10);
      if (!map.has(day)) map.set(day, []);
      map.get(day)!.push(s);
    }
    return [...map.entries()];
  }, [slots]);

  const dayFmt = new Intl.DateTimeFormat(i18n.language, { weekday: "long", day: "numeric", month: "long" });
  const timeFmt = new Intl.DateTimeFormat(i18n.language, { hour: "2-digit", minute: "2-digit" });

  return (
    <Box sx={{ bgcolor: "background.default", pb: 16 }}>
      <Container maxWidth="md" sx={{ pt: { xs: 4, sm: 6 } }}>
        {/* Psychologist header */}
        {psy && (
          <Box sx={{ display: "flex", gap: 2.5, mb: 4 }}>
            <Avatar src={psy.photoUrl} sx={{ width: 64, height: 64, bgcolor: "var(--primary-100)", color: "primary.dark", fontFamily: "var(--font-display)" }}>
              {psy.displayName?.[0]}
            </Avatar>
            <Box>
              <Typography variant="h2" sx={{ fontSize: "1.6rem", mb: 0.5 }}>{psy.displayName}</Typography>
              {psy.bio && <Typography sx={{ fontSize: ".95rem", m: 0 }}>{psy.bio}</Typography>}
              {/* DSA art. 16: melden van onjuiste/onrechtmatige profielinhoud */}
              <Box sx={{ mt: 0.75 }}>
                <ReportProblemDialog entityType="psychologist_profile" entityRef={psychologistId} />
              </Box>
            </Box>
          </Box>
        )}

        {coverage && remaining > 0 && !fundingExhausted && (
          <Box sx={{ mb: 3, p: 2, borderRadius: "var(--radius-md)", bgcolor: "var(--primary-50)", border: "1px solid var(--primary-100)" }}>
            <Typography sx={{ color: "primary.dark", m: 0, fontSize: ".95rem" }}>{t("booking.coverageNote", { remaining, allowance: coverage.allowance })}</Typography>
          </Box>
        )}

        {/* Usage-based employer model: neutral self-pay transition note. Only
            the member sees this — the employer is never told who hit the cap. */}
        {selfPayTransition && (
          <Alert severity="info" sx={{ mb: 3 }}>{t("booking.selfPayNotice")}</Alert>
        )}

        {/* Session type */}
        <Typography variant="h3" sx={{ fontSize: "1.15rem", mb: 1.5 }}>{t("booking.chooseType")}</Typography>
        <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap", gap: 1.5, mb: 4 }}>
          {visibleTypes.map((tp) => {
            const price = priceFor(tp.key);
            return (
              <Chip
                key={tp.key}
                onClick={() => setType(tp.key)}
                variant={type === tp.key ? "filled" : "outlined"}
                color={type === tp.key ? "primary" : "default"}
                label={`${t(`booking.type.${tp.key}`)} · ${tp.min} min · ${price === 0 ? t("booking.free") : coveredFor(tp.key) ? t("booking.covered") : eur(price)}`}
                sx={{ py: 2.25, px: 1, fontSize: ".9rem", borderRadius: "var(--radius-md)" }}
              />
            );
          })}
        </Stack>

        {/* Financiële-informatieplicht (wet patiëntenrechten, 2024): naast de
            prijs vooraf ook expliciet melden dat de zorgverlener niet
            RIZIV-geconventioneerd werkt (vrij tarief; terugbetaling loopt via
            de aanvullende verzekering van de mutualiteit, niet via de
            eerstelijnsconventie). Gap-analyse §2. */}
        <Typography sx={{ color: "text.secondary", fontSize: ".85rem", mt: -3, mb: 4 }}>
          {t("booking.conventionNote")}
        </Typography>
          {(psy as any)?.lateCancellationPolicy === "strict_24h" && (
            <Typography sx={{ fontSize: ".85rem", color: "text.secondary", mt: 0.5, mb: 0 }}>{t("booking.cancelPolicyStrict")}</Typography>
          )}

        {/* Reimbursement preview (provider-gated) */}
        {preview && (
          preview.notEligible ? (
            fundCode === "cm" ? (
              <Alert severity="warning" sx={{ mb: 4 }}>{t("vetting.cmWarningClient")}</Alert>
            ) : (
              <Box sx={{ mb: 4, p: 2, borderRadius: "var(--radius-md)", bgcolor: "var(--surface-alt)", border: "1px solid var(--border)" }}>
                <Typography sx={{ color: "text.secondary", m: 0, fontSize: ".95rem" }}>{t("vetting.notEligibleFund", { fund: preview.fundName })}</Typography>
              </Box>
            )
          ) : preview.perSessionCents > 0 ? (
            <Box sx={{ mb: 4, p: 2, borderRadius: "var(--radius-md)", bgcolor: "var(--primary-50)", border: "1px solid var(--primary-100)" }}>
              <Typography sx={{ color: "primary.dark", m: 0, fontSize: ".95rem" }}>{t("booking.reimbursementNote", { fund: preview.fundName, amount: eur(preview.perSessionCents), youPay: eur(preview.youPayCents) })}</Typography>
            </Box>
          ) : null
        )}

        {/* Slots */}
        <Typography variant="h3" sx={{ fontSize: "1.15rem", mb: 1.5 }}>{t("booking.pickSlot")}</Typography>
        {loadingSlots ? (
          <CircularProgress sx={{ color: "primary.main" }} />
        ) : byDay.length === 0 ? (
          <Typography sx={{ color: "text.secondary" }}>{t("booking.noSlots")}</Typography>
        ) : (
          <Stack spacing={2.5}>
            {byDay.slice(0, 7).map(([day, daySlots]) => (
              <Box key={day}>
                <Typography sx={{ fontWeight: 600, mb: 1, textTransform: "capitalize" }}>
                  {dayFmt.format(new Date(day + "T00:00:00"))}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                  {daySlots.map((s) => (
                    <Button
                      key={s.start}
                      size="small"
                      variant={selected === s.start ? "contained" : "outlined"}
                      onClick={() => { setSelected(s.start); setPendingCheckout(""); }}
                      sx={{ minWidth: 72, py: 0.5 }}
                    >
                      {timeFmt.format(new Date(s.start))}
                    </Button>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}

      </Container>

      {/* Auth gate as a modal so it's front-and-center; booking resumes on success. */}
      <Dialog open={needAuth && !user} onClose={() => { setNeedAuth(false); setWantBook(false); }} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography sx={{ mb: 2, color: "text.secondary" }}>{t("booking.signInToBook")}</Typography>
          <AuthPanel initialMode="register" onSuccess={() => {}} />
        </DialogContent>
      </Dialog>

      {/* Sticky confirm bar. For paid sessions this is the "order with obligation
          to pay" step (WER VI.46 §2): the price recap sits immediately next to
          the button and the button carries the statutory wording + total price. */}
      {selected && (
        <Box sx={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 10, bgcolor: "rgba(250,243,233,0.92)", backdropFilter: "blur(8px)", borderTop: "1px solid var(--border)", px: { xs: 2.5, sm: 3 }, pt: 2, pb: "calc(16px + env(safe-area-inset-bottom))" }}>
          <Box sx={{ maxWidth: 900, mx: "auto" }}>
            {/* A self-paid booking was created while the UI still showed
                "covered" — explain and require an explicit step to payment. */}
            {pendingCheckout && (
              <Alert
                severity="info"
                sx={{ mb: 1.5 }}
                action={<Button size="small" variant="contained" onClick={() => { window.location.href = pendingCheckout; }}>{t("booking.selfPayContinue")}</Button>}
              >
                {t("booking.selfPayNotice")}
              </Alert>
            )}
            {/* WER VI.46 §8 — required, initially unticked checkbox for paid
                sessions that take place within the 14-day withdrawal period. */}
            {needsWithdrawalConsent && (
              <FormControlLabel
                control={<Checkbox checked={withdrawalConsent} onChange={(e) => setWithdrawalConsent(e.target.checked)} size="small" sx={{ mt: -0.5, alignSelf: "flex-start" }} />}
                label={t("booking.withdrawalConsent")}
                sx={{ mb: 1.5, alignItems: "flex-start", "& .MuiFormControlLabel-label": { fontSize: ".85rem", color: "text.secondary" } }}
              />
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Box>
                <Typography sx={{ m: 0, fontSize: ".95rem" }}>
                  {t(`booking.type.${type}`)} · {timeFmt.format(new Date(selected))} · {priceCents === 0 ? t("booking.free") : coveredFor(type) ? t("booking.covered") : eur(priceCents)}
                </Typography>
                {isPaid && (
                  <Typography sx={{ m: 0, fontSize: ".85rem", fontWeight: 600 }}>
                    {t("booking.totalInclFees", { price: eur(priceCents) })}
                  </Typography>
                )}
              </Box>
              <Button
                variant="contained"
                size="large"
                onClick={onConfirm}
                disabled={busy || !!pendingCheckout || (needsWithdrawalConsent && !withdrawalConsent)}
                sx={{ ml: "auto", minWidth: 160 }}
              >
                {busy
                  ? t("funnel.submitting")
                  : isPaid
                    ? t("booking.payObligation", { price: eur(priceCents) })
                    : t("booking.confirm")}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
